import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLocation } from "react-router-dom";
import { translations } from "../modal/translations";
import { validateName, validateEmail } from "./validators";
import { useFormState } from "./useFormState";

export const useInterestForm = (onClose: () => void, type: 'notification' | 'partnership') => {
  const {
    name,
    setName,
    email,
    setEmail,
    message,
    setMessage,
    isSubmitting,
    setIsSubmitting
  } = useFormState();

  const { toast } = useToast();
  const location = useLocation();
  const currentLang = location.pathname.split('/')[1] || 'en';
  const t = translations[currentLang];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateName(name)) {
      toast({
        title: t.error.invalidName.title,
        description: t.error.invalidName.description,
        variant: "destructive",
      });
      return;
    }

    if (!validateEmail(email)) {
      toast({
        title: t.error.invalidEmail.title,
        description: t.error.invalidEmail.description,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { error: dbError } = await supabase
        .from('interested_users')
        .insert([{ name, email, type, message }]);

      if (dbError) throw dbError;

      const { error: emailError } = await supabase.functions.invoke('send-interest-confirmation', {
        body: { 
          to: email, 
          name, 
          type, 
          message,
          language: currentLang
        }
      });

      if (emailError) throw emailError;

      toast({
        title: t.success.title,
        description: t.success.description,
      });
      
      onClose();
      setName("");
      setEmail("");
      setMessage("");
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: t.error.generic.title,
        description: t.error.generic.description,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    name,
    setName,
    email,
    setEmail,
    message,
    setMessage,
    isSubmitting,
    validateName,
    validateEmail,
    handleSubmit,
  };
};